import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { ChangePassword } from "@/components/ChangePassword";
import { HardHat, FileText, MessageCircle, User, Users, CalendarDays, LogOut, FileSignature, Star, Briefcase, NotebookPen, Hammer, BarChart3, BellRing, Sun, Ruler } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/rp";

const CLIENT_NAV = [
  { to: "/commessa", label: "Cantiere", icon: HardHat, id: "cantiere" },
  { to: "/diario", label: "Diario di bordo", short: "Diario", icon: NotebookPen, id: "diario" },
  { to: "/preventivi", label: "Preventivi", icon: FileSignature, id: "preventivi" },
  { to: "/documenti", label: "Documenti", icon: FileText, id: "documenti" },
  { to: "/messaggi", label: "Messaggi", icon: MessageCircle, id: "messaggi", badge: true },
  { to: "/profilo", label: "Profilo", icon: User, id: "profilo" },
];
const ADMIN_NAV = [
  { to: "/admin/oggi", label: "Oggi", icon: Sun, id: "oggi" },
  { to: "/admin/sopralluoghi", label: "Sopralluoghi", short: "Sopral.", icon: Ruler, id: "sopralluoghi" },
  { to: "/admin/commesse", label: "Cantieri", icon: HardHat, id: "cantieri" },
  { to: "/admin/diario", label: "Diario di bordo", short: "Diario", icon: NotebookPen, id: "diario" },
  { to: "/admin/avvisi", label: "Avvisi", icon: BellRing, id: "avvisi" },
  { to: "/admin/amministrazione", label: "Amministrazione", short: "Ammin.", icon: Briefcase, id: "amministrazione" },
  { to: "/admin/subappalti", label: "Subappalti", short: "Subapp.", icon: Hammer, id: "subappalti", restricted: true },
  { to: "/admin/clienti", label: "Clienti", icon: Users, id: "clienti" },
  { to: "/admin/preventivi", label: "Preventivi", icon: FileSignature, id: "preventivi" },
  { to: "/admin/messaggi", label: "Messaggi", icon: MessageCircle, id: "messaggi", badge: true },
  { to: "/admin/agenda", label: "Agenda", icon: CalendarDays, id: "agenda" },
  { to: "/admin/soddisfazione", label: "Soddisfazione", short: "Voti", icon: Star, id: "soddisfazione" },
  { to: "/admin/statistiche", label: "Statistiche", short: "Stat.", icon: BarChart3, id: "statistiche" },
];

const Badge = ({ n, testid }) =>
  n > 0 ? (
    <span data-testid={testid} className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#070707] text-white text-[11px] font-semibold flex items-center justify-center">
      {n > 99 ? "99+" : n}
    </span>
  ) : null;

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [canSub, setCanSub] = useState(false);
  const items = user.role === "admin" ? ADMIN_NAV.filter((it) => !it.restricted || canSub) : CLIENT_NAV;

  useEffect(() => {
    if (user.role === "admin") api.get("/amministrazione/settings/subappalti-access").then((r) => setCanSub(r.data.can_view)).catch(() => {});
  }, [user.role]);

  useEffect(() => {
    const load = () => api.get("/messages/unread").then((r) => setUnread(r.data.unread)).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-white">
      <aside className="hidden min-[900px]:flex fixed inset-y-0 left-0 w-[260px] flex-col border-r border-[#EBEBEB] bg-white px-5 py-8 z-30" data-testid="desktop-sidebar">
        <Logo className="h-8 self-start ml-2" />
        <p className="rp-label ml-2 mt-3">{user.role === "admin" ? "Area staff" : "Area clienti"}</p>
        <nav className="mt-10 flex flex-col gap-1">
          {items.map((it) => (
            <NavLink
              key={it.id}
              to={it.to}
              end={it.end}
              data-testid={`nav-sidebar-item-${it.id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 h-11 px-4 rounded-full text-[15px] transition-colors ${isActive ? "bg-[#070707] text-white" : "text-black hover:bg-[#F7F7F7]"}`
              }
            >
              <it.icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
              {it.label}
              {it.badge && <Badge n={unread} testid="sidebar-unread-badge" />}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto rp-card p-4">
          <p className="rp-label">Connesso come</p>
          <p className="font-medium mt-1 truncate" data-testid="sidebar-user-name">{user.name}</p>
          <p className="text-xs text-[#757575] truncate">{user.email}</p>
          {user.role === "admin" && <ChangePassword className="mt-4" />}
          <button onClick={doLogout} data-testid="sidebar-logout-button" className="mt-3 flex items-center gap-2 text-sm text-[#757575] hover:text-black transition-colors">
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      </aside>

      <header className="min-[900px]:hidden sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-[#EBEBEB] px-5 h-14 flex items-center justify-between">
        <Logo className="h-6" />
        <span className="flex items-center gap-3">{user.role === "admin" && <ChangePassword className="text-xs" />}<span className="rp-label truncate max-w-[120px]">{user.name}</span></span>
      </header>

      <main className="min-[900px]:pl-[260px] pb-28 min-[900px]:pb-0">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 lg:px-12 py-8 lg:py-12">
          <Outlet />
        </div>
      </main>

      <nav className="min-[900px]:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-xl border-t border-[#EBEBEB] flex overflow-x-auto pb-[env(safe-area-inset-bottom)]" data-testid="mobile-bottom-nav">
        {items.map((it) => (
          <NavLink
            key={it.id}
            to={it.to}
            end={it.end}
            data-testid={`nav-mobile-item-${it.id}`}
            className={({ isActive }) => `relative flex-1 min-w-[64px] shrink-0 flex flex-col items-center justify-center gap-1 h-16 text-[10px] sm:text-[11px] font-medium ${isActive ? "text-black" : "text-[#757575]"}`}
          >
            <it.icon className="h-5 w-5" strokeWidth={1.75} />
            {it.short || it.label}
            {it.badge && unread > 0 && (
              <span data-testid="mobile-unread-badge" className="absolute top-2 left-1/2 ml-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#070707] text-white text-[10px] flex items-center justify-center">
                {unread}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
